import React, { useState, useMemo } from 'react';
import {
  StepContainer,
  SectionHeader,
  SectionTitle,
  SectionDivider,
  SectionDescription,
  ButtonContainer,
  NextButton,
  SecondaryButton,
  TotalContainer,
  TotalLabel,
  TotalValue,
  PillContainer,
  Pill,
  FormGroup,
  Label,
} from '../../styles';
import MoneyInput from '../common/MoneyInput';
import { MobileAnalysisFormData } from '../../types';
import { validityOptions } from '../../constants';

interface Step4Props {
  formData: MobileAnalysisFormData;
  updateFormData: (data: Partial<MobileAnalysisFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const Step4_PropertyValues: React.FC<Step4Props> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Parse money value to number
  const parseMoneyValue = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
  };

  // Calculate total
  const totalAmount = useMemo(() => {
    const values = formData.values;
    let total = parseMoneyValue(values.rentValue);

    if (values.isCondominiumEnabled) {
      total += parseMoneyValue(values.condominiumFee);
    }
    if (values.isTaxesEnabled) {
      total += parseMoneyValue(values.taxesValue);
    }
    if (values.isEnergyEnabled) {
      total += parseMoneyValue(values.energyBill);
    }
    if (values.isWaterEnabled) {
      total += parseMoneyValue(values.waterBill);
    }
    if (values.isGasEnabled) {
      total += parseMoneyValue(values.gasBill);
    }

    return total;
  }, [formData.values]);

  // Format total for display
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const updateValues = (updates: Partial<MobileAnalysisFormData['values']>) => {
    updateFormData({
      values: { ...formData.values, ...updates },
    });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const rentValue = parseMoneyValue(formData.values.rentValue);
    if (rentValue <= 0) {
      newErrors.rentValue = 'Valor do aluguel deve ser maior que R$ 0,00';
    }

    if (!formData.values.validity) {
      newErrors.validity = 'Selecione a vigência';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <StepContainer>
      <SectionHeader>
        <SectionTitle>Valores e Vigência</SectionTitle>
        <SectionDivider />
      </SectionHeader>

      <SectionDescription>
        Informe o valor do aluguel e encargos. Use os botões para habilitar/desabilitar cada encargo.
      </SectionDescription>

      {/* Rent Value (required) */}
      <MoneyInput
        label="Valor do Aluguel"
        value={formData.values.rentValue}
        onChange={(value) => updateValues({ rentValue: value })}
        required
        error={errors.rentValue}
      />

      {/* Condominium (toggleable) */}
      <MoneyInput
        label="Condomínio"
        value={formData.values.condominiumFee}
        onChange={(value) => updateValues({ condominiumFee: value })}
        toggleable
        isEnabled={formData.values.isCondominiumEnabled}
        onToggle={(enabled) => updateValues({ isCondominiumEnabled: enabled })}
      />

      {/* Taxes/IPTU (toggleable) */}
      <MoneyInput
        label="IPTU Mensal"
        value={formData.values.taxesValue}
        onChange={(value) => updateValues({ taxesValue: value })}
        toggleable
        isEnabled={formData.values.isTaxesEnabled}
        onToggle={(enabled) => updateValues({ isTaxesEnabled: enabled })}
      />

      {/* Energy Bill (toggleable) */}
      <MoneyInput
        label="Energia"
        value={formData.values.energyBill}
        onChange={(value) => updateValues({ energyBill: value })}
        toggleable
        isEnabled={formData.values.isEnergyEnabled}
        onToggle={(enabled) => updateValues({ isEnergyEnabled: enabled })}
      />

      {/* Water Bill (toggleable) */}
      <MoneyInput
        label="Água"
        value={formData.values.waterBill}
        onChange={(value) => updateValues({ waterBill: value })}
        toggleable
        isEnabled={formData.values.isWaterEnabled}
        onToggle={(enabled) => updateValues({ isWaterEnabled: enabled })}
      />

      {/* Gas Bill (toggleable) */}
      <MoneyInput
        label="Gás Canalizado"
        value={formData.values.gasBill}
        onChange={(value) => updateValues({ gasBill: value })}
        toggleable
        isEnabled={formData.values.isGasEnabled}
        onToggle={(enabled) => updateValues({ isGasEnabled: enabled })}
      />

      {/* Total Display */}
      <TotalContainer>
        <TotalLabel>Valor total da cobertura</TotalLabel>
        <TotalValue>{formatCurrency(totalAmount)}</TotalValue>
      </TotalContainer>

      {/* Validity Selection */}
      <FormGroup style={{ marginTop: 24 }}>
        <Label required>Vigência do Contrato</Label>
        <PillContainer>
          {validityOptions.map((option) => (
            <Pill
              key={option.key}
              type="button"
              selected={formData.values.validity === option.value}
              onClick={() => updateValues({ validity: option.value })}
            >
              {option.text}
            </Pill>
          ))}
        </PillContainer>
        {errors.validity && (
          <span style={{ color: '#FF3B30', fontSize: 12, marginTop: 8, display: 'block' }}>
            {errors.validity}
          </span>
        )}
      </FormGroup>

      <ButtonContainer>
        <SecondaryButton type="button" onClick={onBack}>
          Voltar
        </SecondaryButton>
        <NextButton type="button" onClick={handleNext}>
          Revisar
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default Step4_PropertyValues;
